import { workerRoles } from '../CreateNewWorkScheduleByPeriod.constants';
import { getOption } from '../CreateNewWorkScheduleByPeriod.helpers';

const serviceReasonKeySeparator = '__';

const getTrimmedText = (value) =>
  typeof value === 'string' ? value.trim() : value;

const getNormalizedText = (value) => {
  const trimmedText = getTrimmedText(value);

  return trimmedText ? String(trimmedText).toLocaleLowerCase('ru-RU') : '';
};

export const getServiceName = (service) =>
  service.fullName || service.description || service.name;

export const getServiceOption = (service) => ({
  ...getOption({
    id: service.id,
    name: getServiceName(service),
  }),
  isImmediate: service.isImmediate,
});

export const getServiceReasonKey = (serviceId, reasonId) =>
  `${serviceId}${serviceReasonKeySeparator}${reasonId}`;

const getEntityId = (entity) => {
  if (entity && typeof entity === 'object') {
    return entity.id ?? entity.value;
  }

  return entity;
};

export const getReasonKeysFromServiceSet = (serviceSet) =>
  (Array.isArray(serviceSet?.services) ? serviceSet.services : []).flatMap(
    (service = {}) => {
      const serviceId = service.serviceId ?? service.id;
      const reasonIds = service.reasonIds || service.reasons?.map(getEntityId);

      if (!serviceId || !Array.isArray(reasonIds)) {
        return [];
      }

      return reasonIds.map((reasonId) =>
        getServiceReasonKey(serviceId, reasonId)
      );
    }
  );

const getWorkerFullName = (worker = {}) => {
  const fullName = getTrimmedText(worker.fullName);

  if (fullName) {
    return fullName;
  }

  return [worker.surName, worker.firstName, worker.patrName]
    .map(getTrimmedText)
    .filter(Boolean)
    .join(' ');
};

const getWorkerSpeciality = (worker = {}) =>
  getTrimmedText(worker.specialityName) ||
  getTrimmedText(worker.speciality) ||
  '';

const getWorkerSpecialities = (worker = {}) => {
  if (Array.isArray(worker.specializations)) {
    return worker.specializations.map(getTrimmedText).filter(Boolean);
  }

  if (Array.isArray(worker.specialities)) {
    return worker.specialities.map(getTrimmedText).filter(Boolean);
  }

  const speciality = getWorkerSpeciality(worker);

  return speciality ? [speciality] : [];
};

const getWorkerRole = (worker = {}) => {
  const rawRole = getNormalizedText(
    worker.role || worker.roleName || worker.workerRole || worker.workerType
  );
  const specialitiesText = getNormalizedText(
    getWorkerSpecialities(worker).join(' ')
  );

  if (
    worker.isMaster ||
    rawRole.includes('master') ||
    rawRole.includes('мастер') ||
    specialitiesText.includes('мастер')
  ) {
    return workerRoles.master;
  }

  if (
    worker.isExecutor ||
    worker.isExecuter ||
    rawRole.includes('executor') ||
    rawRole.includes('executer') ||
    rawRole.includes('исполнитель')
  ) {
    return workerRoles.executor;
  }

  return workerRoles.executor;
};

export const normalizeWorker = (worker) => ({
  ...worker,
  id: worker.id,
  fullName: getWorkerFullName(worker),
  specialities: getWorkerSpecialities(worker),
  speciality: getWorkerSpecialities(worker).join(', '),
  role: getWorkerRole(worker),
});

export const getArrayValue = (value) =>
  Array.isArray(value) ? value : [value].filter(Boolean);

export const getSelectionKey = (values = []) =>
  getArrayValue(values).map(String).sort().join('|');

export const getValidSelectedValues = (selectedValues = [], options = []) => {
  const optionValuesByKey = options.reduce((acc, option) => {
    acc.set(String(option.value), option.value);

    return acc;
  }, new Map());

  return getArrayValue(selectedValues)
    .filter((value) => optionValuesByKey.has(String(value)))
    .map((value) => optionValuesByKey.get(String(value)));
};

export const getUniqueValues = (values = []) => {
  const valueSet = new Set();

  return getArrayValue(values).filter((value) => {
    const valueKey = String(value);

    if (valueSet.has(valueKey)) {
      return false;
    }

    valueSet.add(valueKey);
    return true;
  });
};

export const getNormalizedServiceSetSelection = ({
  reasonGroups = [],
  selectedReasonKeys = [],
  selectedServiceIds = [],
}) => {
  const selectedReasonKeySet = new Set(selectedReasonKeys.map(String));
  const selectedServiceIdSet = new Set(selectedServiceIds.map(String));
  const requestParentTypeId = [];
  const requestTypeId = [];
  const groups = reasonGroups
    .map((service) => {
      const serviceReasons = service.reasons || [];
      const selectedReasons = serviceReasons.filter((reason) =>
        selectedReasonKeySet.has(String(reason.value))
      );
      const hasAllReasonsSelected = Boolean(
        serviceReasons.length &&
          selectedReasons.length === serviceReasons.length
      );
      const useParent =
        selectedServiceIdSet.has(String(service.value)) ||
        hasAllReasonsSelected;

      if (!useParent && !selectedReasons.length) {
        return null;
      }

      if (useParent) {
        requestParentTypeId.push(service.value);
      } else {
        requestTypeId.push(...selectedReasons.map((reason) => reason.reasonId));
      }

      return {
        service,
        selectedReasons: useParent ? serviceReasons : selectedReasons,
        useParent,
      };
    })
    .filter(Boolean);

  return {
    groups,
    requestParentTypeId: getUniqueValues(requestParentTypeId),
    requestTypeId: getUniqueValues(requestTypeId),
  };
};

export const getUniqueServiceSets = (serviceSets = []) => {
  const serviceSetIdSet = new Set();

  return serviceSets.filter((serviceSet) => {
    const serviceSetKey = String(serviceSet.id);

    if (serviceSetIdSet.has(serviceSetKey)) {
      return false;
    }

    serviceSetIdSet.add(serviceSetKey);
    return true;
  });
};

export const getTrimmedWorkerText = getTrimmedText;
